import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

export class CameraManager {
  constructor(domElement, requestRender = () => {}) {
    this.domElement = domElement;
    this.requestRender = requestRender;
    this.perspective = new THREE.PerspectiveCamera(42, 1, 0.05, 1000);
    this.orthographic = new THREE.OrthographicCamera(-10, 10, 10, -10, 0.05, 1000);
    this.active = this.perspective;
    this.mode = 'orbit';
    this.orbit = new OrbitControls(this.perspective, domElement);
    this.orbit.enableDamping = true;
    this.orbit.dampingFactor = 0.08;
    this.orbit.minDistance = 2;
    this.orbit.maxDistance = 180;
    this.orbit.maxPolarAngle = Math.PI * 0.49;
    this.orbit.addEventListener('change', () => this.requestRender(500));
    this.top = new OrbitControls(this.orthographic, domElement);
    this.top.enabled = false;
    this.top.enableRotate = false;
    this.top.enableDamping = true;
    this.top.dampingFactor = 0.09;
    this.top.screenSpacePanning = true;
    this.top.minZoom = 0.35;
    this.top.maxZoom = 12;
    this.top.addEventListener('change', () => this.requestRender(500));
    this.keys = new Set();
    this.touchMovement = new Set();
    this.yaw = Math.PI;
    this.pitch = 0;
    this.draggingLook = false;
    this.lastPointer = null;
    this.bounds = null;
    this.#bindEvents();
  }

  resize(width, height) {
    const safeHeight = Math.max(1, height);
    this.perspective.aspect = width / safeHeight;
    this.perspective.updateProjectionMatrix();
    const aspect = width / safeHeight;
    const span = this.orthographic.userData.span ?? 16;
    this.orthographic.left = -span * aspect / 2;
    this.orthographic.right = span * aspect / 2;
    this.orthographic.top = span / 2;
    this.orthographic.bottom = -span / 2;
    this.orthographic.updateProjectionMatrix();
  }

  frameToObject(root) {
    const box = new THREE.Box3().setFromObject(root);
    if (box.isEmpty()) throw new Error('Bounding box empty.');
    this.bounds = box.clone();
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxSize = Math.max(size.x, size.y, size.z);
    const fov = THREE.MathUtils.degToRad(this.perspective.fov);
    const distance = Math.max(maxSize * 1.12, maxSize / (2 * Math.tan(fov / 2)) * 1.1);
    this.perspective.near = Math.max(0.03, distance / 1000);
    this.perspective.far = Math.max(500, distance * 10);
    this.perspective.position.copy(center).add(new THREE.Vector3(distance * 0.66, distance * 0.54, distance * 0.72));
    this.perspective.lookAt(center);
    this.perspective.updateProjectionMatrix();
    this.orbit.target.copy(center);
    this.orbit.update();
    this.orthographic.userData.span = Math.max(size.x, size.z) * 1.28;
    this.orthographic.position.set(center.x, box.max.y + distance, center.z);
    this.orthographic.up.set(0, 0, -1);
    this.orthographic.lookAt(center);
    this.orthographic.updateProjectionMatrix();
    this.top.target.copy(center);
    this.top.update();
    this.requestRender(600);
    return { box, center, size, distance };
  }

  setMode(mode, project) {
    this.mode = mode;
    this.orbit.enabled = mode === 'orbit' || mode === 'section';
    this.top.enabled = mode === 'plan';
    if (mode === 'plan') {
      this.active = this.orthographic;
    } else {
      this.active = this.perspective;
    }
    if (mode === 'walk') {
      const start = project?.cameraStart ?? [0, 1.65, 8];
      this.perspective.position.set(...start);
      const target = new THREE.Vector3(0, 1.65, 0);
      this.perspective.lookAt(target);
      this.yaw = Math.atan2(target.x - start[0], target.z - start[2]);
      this.pitch = 0;
      this.#applyLook();
    } else if (mode === 'fly') {
      const center = this.bounds?.getCenter(new THREE.Vector3()) ?? new THREE.Vector3();
      this.perspective.position.copy(center).add(new THREE.Vector3(6, 7, 9));
      this.perspective.lookAt(center);
      const direction = center.clone().sub(this.perspective.position).normalize();
      this.yaw = Math.atan2(direction.x, direction.z);
      this.pitch = Math.asin(direction.y);
    }
    this.requestRender(500);
    return this.active;
  }

  update(delta, collisionTest = () => false) {
    if (this.mode === 'orbit' || this.mode === 'section') {
      return this.orbit.update();
    }
    if (this.mode === 'plan') return this.top.update();
    if (this.mode !== 'walk' && this.mode !== 'fly') return false;
    const input = new THREE.Vector3();
    const pressed = (key) => this.keys.has(key) || this.touchMovement.has(key);
    if (pressed('KeyW') || pressed('ArrowUp')) input.z += 1;
    if (pressed('KeyS') || pressed('ArrowDown')) input.z -= 1;
    if (pressed('KeyA') || pressed('ArrowLeft')) input.x -= 1;
    if (pressed('KeyD') || pressed('ArrowRight')) input.x += 1;
    if (this.mode === 'fly' && (pressed('KeyE') || pressed('PageUp'))) input.y += 1;
    if (this.mode === 'fly' && (pressed('KeyQ') || pressed('PageDown'))) input.y -= 1;
    if (!input.lengthSq()) return false;
    input.normalize();
    const forward = new THREE.Vector3(Math.sin(this.yaw), 0, Math.cos(this.yaw));
    const right = new THREE.Vector3(forward.z, 0, -forward.x);
    const movement = forward.multiplyScalar(input.z).add(right.multiplyScalar(input.x));
    movement.y += input.y;
    movement.normalize().multiplyScalar((this.mode === 'fly' ? 7 : 3.2) * delta);
    const candidate = this.perspective.position.clone().add(movement);
    if (this.mode === 'walk') {
      candidate.y = this.perspective.position.y;
      if (candidate.y < 1.65) candidate.y = 1.65;
      if (collisionTest(candidate)) return false;
    }
    this.perspective.position.copy(candidate);
    this.requestRender();
    return true;
  }

  setTouchMovement(key, active) {
    if (active) this.touchMovement.add(key);
    else this.touchMovement.delete(key);
  }

  changeWalkFloor(direction) {
    if (this.mode !== 'walk') return;
    const next = this.perspective.position.y + Math.sign(direction) * 3.2;
    this.perspective.position.y = Math.max(1.65, next);
    this.requestRender();
  }

  getDebug() {
    const target = this.mode === 'plan' ? this.top.target : this.orbit.target;
    return {
      mode: this.mode,
      position: this.active.position.toArray().map((value) => Number(value.toFixed(2))),
      target: target.toArray().map((value) => Number(value.toFixed(2)))
    };
  }

  dispose() {
    this.orbit.dispose();
    this.top.dispose();
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
  }

  #applyLook() {
    this.perspective.rotation.order = 'YXZ';
    this.perspective.rotation.y = this.yaw;
    this.perspective.rotation.x = -this.pitch;
  }

  #bindEvents() {
    this.onKeyDown = (event) => this.keys.add(event.code);
    this.onKeyUp = (event) => this.keys.delete(event.code);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    this.domElement.addEventListener('pointerdown', (event) => {
      if (!['walk', 'fly'].includes(this.mode)) return;
      this.draggingLook = true;
      this.lastPointer = [event.clientX, event.clientY];
      this.domElement.setPointerCapture?.(event.pointerId);
    });
    this.domElement.addEventListener('pointermove', (event) => {
      if (!this.draggingLook || !this.lastPointer) return;
      const dx = event.clientX - this.lastPointer[0];
      const dy = event.clientY - this.lastPointer[1];
      this.lastPointer = [event.clientX, event.clientY];
      this.yaw -= dx * 0.004;
      this.pitch = THREE.MathUtils.clamp(this.pitch + dy * 0.003, -1.35, 1.35);
      this.#applyLook();
      this.requestRender();
    });
    const release = () => { this.draggingLook = false; this.lastPointer = null; };
    this.domElement.addEventListener('pointerup', release);
    this.domElement.addEventListener('pointercancel', release);
  }
}
